// =====================================================
// S-I18N-W1a — the ONE process-wide "which language am I rendering in?"
// cell, deliberately dependency-free.
//
// Why a bare module and not React context: `format.js` / `jalali.ts` are
// called from ~144 files, many of them outside a component (event handlers,
// data mappers, `window.toast` payload builders). Threading a hook through
// all of them would break the "signatures stay untouched" contract of
// ADR-A4. i18next owns the *strings*; this cell owns the *number and date*
// side of the same locale, and `LocaleProvider` keeps the two in step.
//
// Nothing here imports i18next — that would make `jalali.ts` (imported by
// the eager bundle and by node-side tests) pull the whole i18n runtime in.
// =====================================================

import {
  INITIAL_ENABLED_LOCALES,
  PLATFORM_DEFAULT_LOCALE,
  directionOfLocale,
  normalisePlatformLocale,
  type PlatformLocaleTag,
} from "./locale-registry";

/** Only technically complete/exposed packs enter the UI runtime. */
export const SUPPORTED_LOCALES: readonly PlatformLocaleTag[] = INITIAL_ENABLED_LOCALES;
export type AppLocale = PlatformLocaleTag;

export const DEFAULT_LOCALE: AppLocale = PLATFORM_DEFAULT_LOCALE;

/** Guest locale cookie (ADR-A3). No personal data — a BCP-47 tag only. */
export const LOCALE_COOKIE = "du_locale";

export const isAppLocale = (v: unknown): v is AppLocale =>
  typeof v === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(v);

/**
 * Normalise anything locale-ish ("ar-SA", "fa-IR", "AR") to a supported tag,
 * or null when it is not one of ours. Used by every link of the ADR-A3 chain.
 */
export const normaliseLocale = (raw: unknown): AppLocale | null => {
  if (typeof raw !== "string" || !raw) return null;
  const platformLocale = normalisePlatformLocale(raw);
  return isAppLocale(platformLocale) ? platformLocale : null;
};

/** Runtime-safe tenant subset. Missing policy preserves the exposed platform set. */
export const tenantEnabledAppLocales = (raw: unknown): AppLocale[] => {
  if (raw === null || raw === undefined) return [...SUPPORTED_LOCALES];
  if (!Array.isArray(raw)) return [DEFAULT_LOCALE];
  const enabled = [...new Set(raw.map(normaliseLocale).filter((locale): locale is AppLocale => Boolean(locale)))];
  return enabled.length > 0 ? enabled : [DEFAULT_LOCALE];
};

let current: AppLocale = DEFAULT_LOCALE;

/** The locale every formatter reads. Never throws, never async. */
export const getLocale = (): AppLocale => current;

/**
 * Set the formatting locale. Returns the value actually applied so callers
 * can tell a rejected tag from an accepted one. `LocaleProvider` is the only
 * production caller; tests call it directly.
 */
export const setLocale = (next: unknown): AppLocale => {
  const parsed = normaliseLocale(next);
  if (parsed) current = parsed;
  return current;
};

// ----- translator seam ----------------------------------------------
//
// `format.js` and `jalali.ts` need a handful of real sentences ("همین الان")
// but must not import the i18next runtime: they are pulled in by the eager
// bundle and by node-side tests where no i18n instance exists. So the runtime
// pushes its `t` in here at boot instead, and until it does every call falls
// back to the Persian source string — which is exactly what these modules
// printed before this package, so nothing regresses when the seam is empty.

export type Translator = (key: string, fallback: string, params?: Record<string, unknown>) => string;

let translator: Translator | null = null;

/** `LocaleProvider` installs i18next's `t` here once the runtime is up. */
export const setTranslator = (fn: Translator | null): void => {
  translator = fn;
};

/**
 * Translate, or return the Persian source when no runtime is attached.
 * `params` (S-I18N-W2) feeds i18next-icu interpolation ({count}, {price}, …)
 * for the non-component callers (public-shared.ts / market-shared.ts) that
 * cannot use the `useTranslation()` hook.
 */
export const translate = (key: string, fallback: string, params?: Record<string, unknown>): string => {
  if (!translator) return fallback;
  try {
    const out = translator(key, fallback, params);
    return typeof out === "string" && out && out !== key ? out : fallback;
  } catch {
    return fallback;
  }
};

/** Direction always comes from script-aware registry metadata. */
export const dirOf = (locale: AppLocale): "rtl" | "ltr" => directionOfLocale(locale);
